import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Bell, Users, Calendar, Handshake, MessageSquare, 
  TrendingUp, Eye, CheckCircle, Clock, AlertCircle,
  UserPlus, Briefcase, HelpCircle, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { Sidebar } from "./Dashboard";
import AdminPromoCodes from "../components/AdminPromoCodes";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const AdminDashboard = () => {
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  
  const [notifications, setNotifications] = useState([]);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [notifRes, engagementRes] = await Promise.all([
        axios.get(`${API}/admin/notifications?limit=50`, {
          headers: getAuthHeaders(),
          withCredentials: true
        }),
        axios.get(`${API}/admin/engagement`, {
          headers: getAuthHeaders(),
          withCredentials: true
        })
      ]);
      setNotifications(notifRes.data.notifications);
      setEngagement(engagementRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/admin/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? {...n, read: true} : n
      ));
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      meeting_scheduled: Calendar,
      meeting_status_update: CheckCircle,
      assistance_requested: HelpCircle,
      new_user: UserPlus,
      referral: Handshake
    };
    return icons[type] || Bell;
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high') return "bg-red-100 text-red-600";
    const colors = {
      meeting_scheduled: "bg-blue-100 text-blue-600",
      meeting_status_update: "bg-emerald-100 text-emerald-600",
      assistance_requested: "bg-purple-100 text-purple-600",
      new_user: "bg-amber-100 text-amber-600"
    };
    return colors[type] || "bg-slate-100 text-slate-600";
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const assistanceRequests = notifications.filter(n => n.type === 'assistance_requested' && !n.read);

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="admin-dashboard-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              iConcierge Command Center
            </h1>
            <p className="text-slate-600">Monitor engagement and assist your network</p>
          </div>

          {/* Priority Alerts */}
          {assistanceRequests.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-purple-900">
                    {assistanceRequests.length} Assistance Request{assistanceRequests.length > 1 ? 's' : ''} Pending
                  </p>
                  <p className="text-sm text-purple-700">Providers need your help with introductions or marketing</p>
                </div>
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
                  View All
                </Button>
              </div>
            </motion.div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <Users className="w-6 h-6 text-primary" />
                <Badge className="bg-emerald-50 text-emerald-700">Active</Badge>
              </div>
              <p className="text-3xl font-bold text-slate-900">{engagement?.users?.active || 0}</p>
              <p className="text-sm text-slate-500">of {engagement?.users?.total || 0} users</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-slate-200 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <Calendar className="w-6 h-6 text-blue-500" />
                <Badge className="bg-amber-50 text-amber-700">{engagement?.meetings?.pending || 0} pending</Badge>
              </div>
              <p className="text-3xl font-bold text-slate-900">{engagement?.meetings?.total || 0}</p>
              <p className="text-sm text-slate-500">Total meetings</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl border border-slate-200 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <Handshake className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="text-3xl font-bold text-slate-900">{engagement?.referrals?.total || 0}</p>
              <p className="text-sm text-slate-500">Total referrals</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl border border-slate-200 p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <HelpCircle className="w-6 h-6 text-purple-500" />
                {engagement?.meetings?.assistance_requests > 0 && (
                  <Badge className="bg-purple-50 text-purple-700">Action needed</Badge>
                )}
              </div>
              <p className="text-3xl font-bold text-slate-900">{engagement?.meetings?.assistance_requests || 0}</p>
              <p className="text-sm text-slate-500">Assistance requests</p>
            </motion.div>
          </div>

          {/* Promo Codes Management (admin-only — backend enforces 403) */}
          <div className="mb-6">
            <AdminPromoCodes />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Notifications Feed */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-slate-600" />
                    <h2 className="font-semibold text-slate-900">Activity Feed</h2>
                    {unreadNotifications.length > 0 && (
                      <Badge className="bg-primary text-white">{unreadNotifications.length}</Badge>
                    )}
                  </div>
                </div>

                <ScrollArea className="h-[500px]">
                  {loading ? (
                    <div className="p-4 space-y-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex gap-4 animate-pulse">
                          <div className="w-10 h-10 bg-slate-200 rounded-lg"></div>
                          <div className="flex-1">
                            <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : notifications.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {notifications.map((notif) => {
                        const NotifIcon = getNotificationIcon(notif.type);
                        return (
                          <div
                            key={notif.notification_id}
                            className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.read ? 'bg-blue-50/50' : ''}`}
                            onClick={() => markAsRead(notif.notification_id)}
                          >
                            <div className="flex gap-4">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getNotificationColor(notif.type, notif.priority)}`}>
                                <NotifIcon className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="font-medium text-slate-900">{notif.title}</p>
                                  {!notif.read && (
                                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{notif.message}</p>
                                {notif.from_user && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <Avatar className="w-6 h-6">
                                      <AvatarImage src={notif.from_user.picture} />
                                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                        {notif.from_user.name?.charAt(0)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm text-slate-500">{notif.from_user.name}</span>
                                  </div>
                                )}
                                <p className="text-xs text-slate-400 mt-2">
                                  {new Date(notif.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-600">No notifications yet</p>
                      <p className="text-sm text-slate-500">Activity from your network will appear here</p>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>

            {/* Meeting Purposes Breakdown */}
            <div>
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Meeting Purposes
                </h3>
                
                {engagement?.meeting_purposes && Object.keys(engagement.meeting_purposes).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(engagement.meeting_purposes).map(([purpose, count]) => (
                      <div key={purpose} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            purpose === 'referral' ? 'bg-blue-500' :
                            purpose === 'partnership' ? 'bg-purple-500' :
                            purpose === 'collaboration' ? 'bg-emerald-500' :
                            'bg-amber-500'
                          }`}></div>
                          <span className="text-slate-700 capitalize">{purpose}</span>
                        </div>
                        <span className="font-semibold text-slate-900">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No meeting data yet</p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      View All Users
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Pending Meetings
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4" />
                      Assistance Queue
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
